import React, { useState, type PropsWithChildren } from "react";

import { styled } from "@linaria/react";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { CaretDownIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

import RotateWithTransition from "../animations/RotateWithTransition";
import FlexItem from "../containers/FlexItem";
import FlexWrapper, { AlignItems, FlexDirection } from "../containers/FlexWrapper";
import HorizontalDivider from "../dividers/HorizontalDivider";
import Icon, { IconVariant } from "../icons/Icon";
import Text, { TextSize, TextVariant, TextWeight } from "../text/Text";

export enum AccordionVariant {
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
}

export enum AccordionSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export interface AccordionProps {
  variant?: AccordionVariant;
  size?: AccordionSize;
  icon?: PhosphorIcon;
  isTrailingIcon?: boolean;
  header: string;
  subheader?: string;
  metric?: React.ReactNode;
  borderRadius?: number | string;
  padding?: string | number;
  isOpenInitial?: boolean;
  /** Controlled mode: external open state */
  isOpen?: boolean;
  /** Controlled mode: callback when toggle is clicked */
  onToggle?: () => void;
}

const AccordionWrapper = withTheme(styled.div<
  PropsWithTheme<{ $borderRadius?: number | string; $variant: AccordionVariant; $isOpen: boolean }>
>`
  width: 100%;
  background-color: ${({ theme, $variant }) => {
    return match($variant)
      .with(AccordionVariant.PRIMARY, () => theme.color.background.primary)
      .with(AccordionVariant.SECONDARY, () => theme.color.background.secondary)
      .with(AccordionVariant.TERTIARY, () => theme.color.background.tertiary)
      .exhaustive();
  }};
  border: 0.5px solid
    ${({ theme, $isOpen }) => ($isOpen ? theme.color.border.secondary : theme.color.border.primary)};
  border-radius: ${({ $borderRadius }) =>
    $borderRadius === undefined ? "5px" : getCssSizeValue($borderRadius)};
  overflow: hidden;
  overscroll-behavior: auto;

  &:hover {
    border-color: ${({ theme, $variant }) => {
      return match($variant)
        .with(AccordionVariant.PRIMARY, () => theme.color.border.secondary)
        .with(AccordionVariant.SECONDARY, () => theme.color.border.secondary)
        .with(AccordionVariant.TERTIARY, () => theme.color.border.secondary)
        .exhaustive();
    }};
  }
`);

const AccordionHeader = withTheme(styled.button<
  PropsWithTheme<{ $isOpen: boolean; $variant: AccordionVariant; $size: AccordionSize }>
>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${({ $size }) => {
    return match($size)
      .with(AccordionSize.SMALL, () => "8px")
      .with(AccordionSize.MEDIUM, () => "12px")
      .with(AccordionSize.LARGE, () => "16px")
      .exhaustive();
  }};
  background-color: transparent;
  cursor: pointer;
  transition: background-color 0.075s ease-in-out;
`);

const AccordionContent = withTheme(styled.div<PropsWithTheme<{ $isOpen: boolean }>>`
  display: grid;
  grid-template-rows: ${({ $isOpen }) => ($isOpen ? "1fr" : "0fr")};
  transition: grid-template-rows 0.15s ease-in-out;
`);

const AccordionContentInner = withTheme(styled.div<PropsWithTheme>`
  overflow: hidden;
  overscroll-behavior: auto;
`);

const Accordion = ({
  children,
  variant = AccordionVariant.PRIMARY,
  size = AccordionSize.MEDIUM,
  icon,
  isTrailingIcon = false,
  header,
  subheader,
  metric,
  borderRadius,
  padding = "12px",
  isOpenInitial = false,
  isOpen: controlledIsOpen,
  onToggle: controlledOnToggle,
}: PropsWithChildren<AccordionProps>) => {
  const [internalIsOpen, setInternalIsOpen] = useState(isOpenInitial);

  // Support controlled and uncontrolled modes
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleToggle = () => {
    if (isControlled) {
      controlledOnToggle?.();
    } else {
      setInternalIsOpen((prev) => !prev);
    }
  };

  return (
    <AccordionWrapper $borderRadius={borderRadius} $variant={variant} $isOpen={isOpen}>
      <AccordionHeader
        $isOpen={isOpen}
        $variant={variant}
        $size={size}
        onClick={handleToggle}
        type="button"
      >
        <FlexWrapper alignItems={AlignItems.CENTER} gap={8}>
          {icon && !isTrailingIcon && (
            <Icon
              component={icon}
              variant={isOpen ? IconVariant.PRIMARY : IconVariant.SECONDARY}
              size={16}
            />
          )}
          <FlexWrapper direction={FlexDirection.COLUMN} gap={2}>
            <Text
              variant={isOpen ? TextVariant.PRIMARY : TextVariant.SECONDARY}
              weight={TextWeight.MEDIUM}
            >
              {header}
            </Text>
            {subheader && (
              <Text
                size={TextSize.BODY_SM}
                variant={isOpen ? TextVariant.SECONDARY : TextVariant.TERTIARY}
              >
                {subheader}
              </Text>
            )}
          </FlexWrapper>
          {icon && isTrailingIcon && (
            <Icon
              component={icon}
              variant={isOpen ? IconVariant.PRIMARY : IconVariant.SECONDARY}
              size={16}
            />
          )}
        </FlexWrapper>
        <FlexWrapper alignItems={AlignItems.CENTER} gap={8}>
          {metric && metric}
          <RotateWithTransition isRotated={isOpen} deg={-180}>
            <Icon
              component={CaretDownIcon}
              variant={isOpen ? IconVariant.PRIMARY : IconVariant.SECONDARY}
              size={12}
            />
          </RotateWithTransition>
        </FlexWrapper>
      </AccordionHeader>
      {isOpen && (
        <FlexItem shrink={0} fillWidth>
          <HorizontalDivider />
        </FlexItem>
      )}
      <AccordionContent $isOpen={isOpen}>
        <AccordionContentInner>
          <FlexWrapper direction={FlexDirection.COLUMN} gap={12} padding={padding}>
            {children}
          </FlexWrapper>
        </AccordionContentInner>
      </AccordionContent>
    </AccordionWrapper>
  );
};

export default Accordion;
